import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function UserDetail({ token, apiBaseUrl }) {
  const { email } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Form states
  const [newFingerprint, setNewFingerprint] = useState('');
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => {
    fetchUserDetails();
  }, [email]);

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diffSeconds = Math.floor((now - lastUpdated) / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    return lastUpdated.toLocaleTimeString();
  };

  const handleManualRefresh = () => {
    fetchUserDetails(true);
  };

  const fetchUserDetails = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await axios.get(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('User details response:', response.data);

      if (!response.data) {
        setError('No data received from server');
      } else if (!response.data.profile) {
        setError('Invalid data format received');
        console.error('Missing profile in response:', response.data);
      } else {
        setUser(response.data);
        setLastUpdated(new Date());
        setError('');
      }
    } catch (err) {
      setError('Failed to load user details');
      console.error('Error fetching user details:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleTransferMachine = async (e) => {
    e.preventDefault();
    if (!newFingerprint.trim()) {
      alert('Please enter a machine fingerprint');
      return;
    }

    setUpdating(true);

    try {
      await axios.put(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}`, {
        transfer_machine: newFingerprint.trim(),
        beta_user_id: user.profile.beta_user_id,
        license_key: user.profile.license_key
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert('Machine transfer completed');
      setNewFingerprint('');
      fetchUserDetails(true);
    } catch (err) {
      alert('Failed to transfer machine');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!adminNote.trim()) return;

    setUpdating(true);

    try {
      await axios.put(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}`, {
        admin_note: adminNote.trim()
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert('Note added successfully');
      setAdminNote('');
      fetchUserDetails(true);
    } catch (err) {
      alert('Failed to add note');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleRevokeUser = async () => {
    if (!window.confirm(`Are you sure you want to revoke access for ${email}?`)) {
      return;
    }

    try {
      await axios.post(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}/revoke`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert('User access revoked successfully');
      navigate('/users');
    } catch (err) {
      alert('Failed to revoke user access');
      console.error(err);
    }
  };

  const handleReactivateUser = async () => {
    if (!window.confirm(`Are you sure you want to reactivate access for ${email}?`)) {
      return;
    }

    try {
      await axios.post(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}/reactivate`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert('User access reactivated successfully');
      fetchUserDetails(true);
    } catch (err) {
      alert('Failed to reactivate user access');
      console.error(err);
    }
  };

  const handleRefreshStatus = async () => {
    setUpdating(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}/refresh-status`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.status === 'active') {
        alert(response.data.message);
        fetchUserDetails(true);
      } else {
        alert(response.data.message || 'Status unchanged');
      }
    } catch (err) {
      alert('Failed to refresh status');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Treat 'expired' as 'active' for lifetime licenses
  const getEffectiveStatus = (status) => {
    return status === 'expired' ? 'active' : status;
  };

  const getStatusBadge = (status) => {
    const effectiveStatus = getEffectiveStatus(status);
    const colors = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[effectiveStatus] || colors.inactive}`}>
        {effectiveStatus || 'inactive'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center">
        <div className="text-red-600">{error || 'User not found'}</div>
        <button
          onClick={() => navigate('/users')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const effectiveStatus = getEffectiveStatus(user.profile.status);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/users')}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          Back to Users
        </button>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{user.profile.email}</h1>
            <p className="text-gray-600">{user.profile.beta_user_id}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {refreshing ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              Refresh
            </button>
            {lastUpdated && (
              <span className="text-xs text-gray-400">
                {formatLastUpdated()}
              </span>
            )}
            {getStatusBadge(user.profile.status)}
            {user.profile.status === 'pending' && (
              <button
                onClick={handleRefreshStatus}
                disabled={updating}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {updating ? 'Syncing...' : 'Sync Status'}
              </button>
            )}
            {effectiveStatus === 'active' && (
              <button
                onClick={handleRevokeUser}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                Revoke Access
              </button>
            )}
            {effectiveStatus === 'revoked' && (
              <button
                onClick={handleReactivateUser}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                Reactivate
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Profile */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h3>

              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.profile.email}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">License Key</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{user.profile.license_key}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">License Type</dt>
                  <dd className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                      Lifetime
                    </span>
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Purchase Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(user.profile.registration_date)}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Machine Information */}
          <div className="mt-6 bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Machine Information</h3>

              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Fingerprint</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono break-all">
                    {user.machine_info.fingerprint || 'N/A'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Platform</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.machine_info.platform || 'N/A'}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Client Version</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.machine_info.client_version || 'N/A'}</dd>
                </div>
              </dl>
            </div>
          </div>

        </div>

        {/* Admin Actions */}
        <div className="space-y-6">
          {/* Transfer Machine */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Transfer to New Machine</h3>

              <form onSubmit={handleTransferMachine} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    New Machine Fingerprint
                  </label>
                  <input
                    type="text"
                    value={newFingerprint}
                    onChange={(e) => setNewFingerprint(e.target.value)}
                    placeholder="Enter new machine fingerprint"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <button
                  type="submit"
                  disabled={updating || !newFingerprint.trim()}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                >
                  {updating ? 'Transferring...' : 'Transfer License'}
                </button>
              </form>
            </div>
          </div>

          {/* Add Admin Note */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Admin Note</h3>

              <form onSubmit={handleAddNote} className="space-y-4">
                <div>
                  <textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Enter admin note..."
                    rows="3"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <button
                  type="submit"
                  disabled={updating || !adminNote.trim()}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  {updating ? 'Adding...' : 'Add Note'}
                </button>
              </form>
            </div>
          </div>

          {/* Admin Notes History */}
          {user.admin_notes && user.admin_notes.length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Admin Notes</h3>

                <div className="space-y-3">
                  {user.admin_notes.map((note, index) => (
                    <div key={index} className="border-l-4 border-blue-400 bg-blue-50 p-4">
                      <div className="text-sm text-gray-900">{note.note}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        By {note.added_by} on {formatDate(note.added_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserDetail;
